import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { acceptanceTerms, brokerQuotes } from '@/shared/api/resources';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';
import { RichTextView } from '@/shared/ui/RichTextEditor';
import { useToast } from '@/shared/ui/useToast';

/**
 * Pedido de emissao da corretora: o dialogo "Termo e declaracao" do OnPoint (NCT-178 a
 * NCT-180), com "Leia os termos para a solicitacao de emissao da apolice", a caixa
 * "Declaro que li e estou ciente das condicoes acima." e os botoes Desistir e
 * Prosseguir.
 *
 * O texto e o termo de aceite do tipo emissao da seguradora (P-USU-2). Sem versao
 * vigente, a caixa nao e exigida e o pedido segue. Prosseguir grava a declaracao na
 * cotacao e abre o pedido: a cotacao passa a "Aguardando Emissao (Seguradora)", e a
 * emissao continua da seguradora.
 */
export function IssuanceRequestDialog({
  operationId,
  open,
  onClose,
}: {
  operationId: string;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [declarou, setDeclarou] = useState(false);

  const termo = useQuery({
    queryKey: ['acceptance-term', 'emissao'],
    queryFn: ({ signal }) => acceptanceTerms.current('emissao', signal),
    enabled: open,
  });
  const vigente = termo.data ?? null;

  const pedir = useMutation({
    mutationFn: async () => {
      if (!vigente) return brokerQuotes.requestIssuance(operationId);
      const declaracao = await acceptanceTerms.approve(vigente.id, {
        operation_id: operationId,
      });
      return brokerQuotes.requestIssuance(operationId, declaracao.id);
    },
    onSuccess: () => {
      toast.success('Solicitacao de emissao de apolice realizada com sucesso!');
      setDeclarou(false);
      void queryClient.invalidateQueries({ queryKey: ['operations'] });
      void queryClient.invalidateQueries({ queryKey: ['quote-timeline', operationId] });
      onClose();
    },
  });

  const bloqueado = termo.isPending || (vigente !== null && !declarou);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Termo e declaracao"
      description="Leia os termos para a solicitacao de emissao da apolice"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pedir.isPending}>
            Desistir
          </Button>
          <Button
            loading={pedir.isPending}
            disabled={bloqueado}
            onClick={() => pedir.mutate()}
          >
            Prosseguir
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {termo.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar o termo de emissao">
            {termo.error.message}
          </Alert>
        ) : null}
        {pedir.isError ? (
          <Alert tone="danger" title="Nao foi possivel pedir a emissao">
            {pedir.error.message}
          </Alert>
        ) : null}
        {termo.isSuccess && vigente === null ? (
          <Alert tone="info" title="Sem termo de emissao vigente">
            A seguradora ainda nao publicou o termo de emissao: o pedido segue sem a
            declaracao. A emissao continua sendo feita pela seguradora.
          </Alert>
        ) : null}
        {vigente ? (
          <>
            <div className="max-h-72 overflow-y-auto rounded-md border border-line p-3">
              <p className="mb-2 text-sm font-semibold text-ink-900">{vigente.title}</p>
              <RichTextView html={vigente.body} />
            </div>
            <label className="flex cursor-pointer items-start gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                checked={declarou}
                onChange={(event) => setDeclarou(event.target.checked)}
                className="mt-0.5 size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
              />
              Declaro que li e estou ciente das condicoes acima.
            </label>
          </>
        ) : null}
        <p className="text-2xs text-ink-500">
          O pedido nao emite a apolice: a cotacao passa a Aguardando Emissao
          (Seguradora), e a seguradora confere e emite. As validacoes da emissao ainda
          sao regra pendente.
        </p>
      </div>
    </Modal>
  );
}
